import React from "react";

import "./css/ResumePage.css";
import { downloadResumePdf, downloadResumeText } from "./resumeDownload";

export default function ResumePage() {
  return (
    <div className="wrapper-resume">
      <h1 className="title">Resume</h1>
      <div className="content">
        <div className="resume-actions">
          <button type="button" className="resume-download" onClick={downloadResumeText}>
            Download Text
          </button>
          <button type="button" className="resume-download resume-download-pdf" onClick={downloadResumePdf}>
            Download PDF
          </button>
        </div>
        <article className="resume-doc">
          <div className="resume-masthead">
            <h2 className="resume-name">Ovidiu Mura</h2>
            <p className="resume-role">
              Senior Software Engineer | Python | Java | TypeScript | Rust | C++
              | Cloud | Algorithms | MCP | Debugging &amp; Performance
            </p>
            <p className="resume-meta">
              Portland, Oregon, United States | Remote | Contractor
            </p>
            <p className="resume-contacts">
              <a href="tel:9719909319">(971) 990-9319</a>
              <span className="resume-sep">|</span>
              <a href="mailto:ovidiu.mura@yahoo.com">ovidiu.mura@yahoo.com</a>
              <span className="resume-sep">|</span>
              <a
                href="https://www.linkedin.com/in/ovidiu-m-200573107"
                target="_blank"
                rel="noreferrer"
              >
                LinkedIn
              </a>
              <span className="resume-sep">|</span>
              <a href="https://github.com/ovimura" target="_blank" rel="noreferrer">
                GitHub
              </a>
            </p>
          </div>

          <section className="resume-section">
            <h3>Professional Summary</h3>
            <p>
              Senior Software Engineer with 15+ years of production experience
              across bug fixing, feature implementation, codebase refactoring,
              and performance optimization. Strong in Python, Java,
              TypeScript/JavaScript, Rust, and C++, with additional Go, plus a
              CS foundation (B.S. and M.S.) in algorithms, data structures, and
              systems. Implemented a Rust program at PacteraEdge to process CSV
              data files as part of operational data workflows.
            </p>
            <p>
              Recent work includes building Model Context Protocol (MCP)
              servers and tools so AI agents can call real systems through
              structured contracts; debugging agent and tool failures; and
              producing maintainable, testable software that can serve as a
              reference solution. Previously evaluated AI-generated code for
              correctness, quality, maintainability, and performance
              (DataAnnotation.tech) across Python, JavaScript, C++, C#, and SQL.
            </p>
            <p>
              Comfortable designing reproducible engineering work: clear
              problem statements, deterministic checks (tests, expected
              outputs, root cause analysis), and golden implementations. Experienced on
              large-scale distributed codebases (Bank of America, Adidas, Nike,
              UPS) and on remote, cross-functional teams with written design,
              code review, and production support.
            </p>
          </section>

          <section className="resume-section">
            <h3>Core Skills</h3>
            <dl className="resume-skills">
              <div>
                <dt>Languages</dt>
                <dd>
                  Python 3, Java, TypeScript, JavaScript, Rust, C++, Go, C#, SQL
                </dd>
              </div>
              <div>
                <dt>Software Engineering</dt>
                <dd>
                  Bug fixing / root cause analysis, feature implementation, codebase
                  refactoring, performance optimization, unit and automated
                  tests, golden / reference implementations
                </dd>
              </div>
              <div>
                <dt>Algorithms &amp; Systems</dt>
                <dd>
                  Data structures, algorithms, complexity-minded performance
                  tuning, distributed systems, scalability, API / service design
                </dd>
              </div>
              <div>
                <dt>MCP &amp; AI Agents</dt>
                <dd>
                  MCP tool servers, agentic workflows, LangChain / LangGraph,
                  LLM tool use, AI coding agents, evaluating generated code vs
                  tests and specs
                </dd>
              </div>
              <div>
                <dt>Cloud &amp; Delivery</dt>
                <dd>
                  AWS, Azure, GCP, Docker, Kubernetes, Terraform, CI/CD, Linux,
                  event-driven services, microservices
                </dd>
              </div>
            </dl>
          </section>

          <section className="resume-section">
            <h3>Key Projects</h3>

            <div className="resume-job">
              <div className="resume-job-top">
                <h4>MCP Tool Servers &amp; Agent-Solvable Engineering Work</h4>
                <span>TEKsystems / Symbotic</span>
              </div>
              <ul>
                <li>
                  Built MCP servers/tools and Python/TypeScript services so AI
                  agents discover and invoke real APIs under structured
                  contracts (auth, retries, error handling).
                </li>
                <li>
                  Debugged complex agent/runtime failures: bad tool arguments,
                  context loss, authorization, and integration defects;
                  converted them into durable code and contract fixes.
                </li>
                <li>
                  Implemented features and refactored prototypes into reviewed,
                  documented, testable services suitable as reference
                  implementations.
                </li>
              </ul>
              <p className="resume-tech">
                Tech: Python, TypeScript, JavaScript, MCP, REST APIs, Docker,
                Kubernetes, GKE, GCP
              </p>
            </div>

            <div className="resume-job">
              <div className="resume-job-top">
                <h4>Agent Workflows, Tests &amp; Human-Verified Solutions</h4>
                <span>US Tech Solutions</span>
              </div>
              <ul>
                <li>
                  Built LangChain / LangGraph / MCP workflows where agents call
                  external systems; added logging, tests, security controls, and
                  human-approval gates so outcomes are checkable.
                </li>
                <li>
                  Used AI coding agents for implementation and debugging, then
                  validated generated changes with tests and technical review.
                </li>
              </ul>
              <p className="resume-tech">
                Tech: Python, TypeScript, LangChain, LangGraph, MCP, LLMs, AWS,
                REST APIs, Docker, Kubernetes
              </p>
            </div>

            <div className="resume-job">
              <div className="resume-job-top">
                <h4>
                  AI Coding Evaluation: Correctness, Maintainability, Performance
                </h4>
                <span>DataAnnotation.tech</span>
              </div>
              <ul>
                <li>
                  Wrote and reviewed programming solutions for an AI-powered
                  coding platform; judged code on correctness, quality,
                  maintainability, and performance.
                </li>
                <li>
                  Worked across Python, JavaScript, HTML, C++, C#, and SQL —
                  identifying bugs, weak abstractions, and faster alternatives.
                </li>
              </ul>
              <p className="resume-tech">
                Tech: Python, JavaScript, C++, C#, SQL, Code Review
              </p>
            </div>

            <div className="resume-job">
              <div className="resume-job-top">
                <h4>Rust CSV Data Processing</h4>
                <span>PacteraEdge — UPS / Markaaz</span>
              </div>
              <ul>
                <li>
                  Implemented a Rust program to process CSV data files for
                  operational data workflows — parsing records, transforming
                  fields, and producing deterministic outputs for downstream
                  pipelines.
                </li>
                <li>
                  Used Rust for performance-minded file processing alongside
                  Python/Spark pipelines and Java/Python automation on the same
                  project.
                </li>
              </ul>
              <p className="resume-tech">
                Tech: Rust, CSV, Python, Java, Spark / PySpark, Data Processing
              </p>
            </div>
          </section>

          <section className="resume-section">
            <h3>Professional Experience</h3>

            <div className="resume-job">
              <div className="resume-job-top">
                <h4>TEKsystems / Symbotic</h4>
                <span>Mar 2026 – Aug 2026</span>
              </div>
              <p className="resume-job-title">Senior Software Engineer</p>
              <ul>
                <li>
                  Implement and maintain production Python/TypeScript services;
                  own bug fixes, features, and refactors through tests and
                  review.
                </li>
                <li>
                  Design MCP tools that expose real systems to AI agents; debug
                  tool-use and runtime failures; improve contracts and
                  performance.
                </li>
                <li>
                  Collaborate remotely with distributed engineering teams on
                  design tradeoffs, code review, and operational reliability.
                </li>
              </ul>
            </div>

            <div className="resume-job">
              <div className="resume-job-top">
                <h4>US Tech Solutions</h4>
                <span>Apr 2025 – Feb 2026</span>
              </div>
              <p className="resume-job-title">Software Engineer</p>
              <ul>
                <li>
                  Delivered Python backend features and MCP/LangGraph agent
                  workflows; investigated defects and shipped maintainable
                  fixes.
                </li>
                <li>
                  Added tests, logging, and human-in-the-loop checks so agent
                  behavior can be verified deterministically.
                </li>
                <li>
                  Deployed containerized services on AWS; refactored evaluation
                  prototypes into reusable components.
                </li>
              </ul>
            </div>

            <div className="resume-job">
              <div className="resume-job-top">
                <h4>Highlight Technologies</h4>
                <span>Mar 2024 – Mar 2025</span>
              </div>
              <p className="resume-job-title">
                Software Engineer / Full-Stack Developer
              </p>
              <ul>
                <li>
                  Implemented TypeScript/Python product features; refactored and
                  optimized event-driven AWS services (DynamoDB, SNS, SQS).
                </li>
                <li>
                  Debugged application, data, and deployment issues;
                  Terraform/CI/CD; production support.
                </li>
              </ul>
            </div>

            <div className="resume-job">
              <div className="resume-job-top">
                <h4>DataAnnotation.tech</h4>
                <span>Jan 2024 – Feb 2024</span>
              </div>
              <p className="resume-job-title">Software Engineer</p>
              <ul>
                <li>
                  Created and evaluated coding solutions for AI
                  training/evaluation: correctness, style, maintainability, and
                  performance.
                </li>
                <li>
                  Reviewed multi-language implementations (Python, JavaScript,
                  C++, C#, SQL) against expected behavior.
                </li>
              </ul>
            </div>

            <div className="resume-job">
              <div className="resume-job-top">
                <h4>Apex Systems / Bank of America</h4>
                <span>Dec 2021 – Dec 2023</span>
              </div>
              <p className="resume-job-title">Application Architect</p>
              <ul>
                <li>
                  Led design and refactoring of large-scale distributed
                  financial platforms (Java, Python, C#, SQL, AWS/Azure).
                </li>
                <li>
                  Code review, root cause analysis, performance/scalability, and production
                  support; promoted only changes that met maintainability and
                  security bars.
                </li>
              </ul>
            </div>

            <div className="resume-job">
              <div className="resume-job-top">
                <h4>PacteraEdge — UPS / Markaaz</h4>
                <span>Jan 2020 – Nov 2021 · Portland, OR</span>
              </div>
              <p className="resume-job-title">Full-Stack Developer</p>
              <ul>
                <li>
                  Implemented a Rust program to process CSV data files for
                  operational workflows; also delivered Java/Python automation,
                  Node.js APIs, and Spark/PySpark pipelines.
                </li>
                <li>
                  Debugged API, data, and deployment issues in cloud B2C
                  systems.
                </li>
              </ul>
            </div>

            <div className="resume-job">
              <div className="resume-job-top">
                <h4>Infosys — Adidas</h4>
                <span>Apr 2018 – Dec 2019 · Portland, OR</span>
              </div>
              <p className="resume-job-title">Technology Lead</p>
              <ul>
                <li>
                  Production defect resolution and platform enhancements with
                  globally distributed (remote) teams.
                </li>
                <li>
                  Specs, test plans, go-live docs; Docker/Kubernetes; mentored
                  engineers.
                </li>
              </ul>
            </div>

            <div className="resume-job">
              <div className="resume-job-top">
                <h4>Earlier Experience</h4>
              </div>
              <ul>
                <li>
                  <strong>Intel — Portland, OR</strong> | Python Developer /
                  Validation / Firmware | 2016 – 2018. C++ and Python tooling,
                  REST APIs, automated tests, performance-oriented validation,
                  Linux.
                </li>
                <li>
                  <strong>Nike — Portland, OR</strong> | Software Engineer |
                  2015 – 2016. Java test automation; JMeter API performance
                  testing during AWS migration; Jenkins CI.
                </li>
                <li>
                  <strong>McKesson Health Solutions — Portland, OR</strong> |
                  Software Engineer | 2012 – 2015. Java/Spring production
                  applications; APIs; SQL; full SDLC in regulated healthcare.
                </li>
                <li>
                  <strong>Fiserv — Corvallis, OR</strong> | Software Engineer |
                  2010 – 2012. Java financial-platform software; Oracle/SQL
                  Server; production delivery.
                </li>
              </ul>
            </div>
          </section>

          <section className="resume-section">
            <h3>Education</h3>
            <ul className="resume-plain">
              <li>
                <strong>Master of Science (M.S.) in Computer Science</strong> —
                Portland State University | 2017 – 2020
              </li>
              <li>
                <strong>Bachelor of Science (B.S.) in Computer Science</strong> —
                Portland State University | 2006 – 2009
              </li>
              <li>
                <strong>Graduate Certificate in Computer Science</strong> —
                Portland State University | 2020
              </li>
            </ul>
          </section>

          <section className="resume-section">
            <h3>Certifications</h3>
            <ul className="resume-plain">
              <li>
                Production AI Agents with LangChain + LangGraph — 2026 (Udemy)
              </li>
              <li>Oracle Certified Professional — Java SE6 Programmer</li>
              <li>Certified Deep Learning Specialization</li>
              <li>HashiCorp Vault Certification</li>
            </ul>
          </section>
        </article>
      </div>
    </div>
  );
}
